import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { AccountService } from '@arkius/shared-api';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SessionService } from '@arkius/shared-services';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { Storage } from '@constants/storage';
import { environment } from '@shared-envs';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';

export const LOAD_HISTORY_EVENT = 'LoadHistory';
export const RECEIVE_MESSAGE_EVENT = 'ReceiveMessage';
export const SEARCH_CONVERSATIONS_METHOD = 'SearchConversations';
export const RECEIVE_CONVERSATION_SEARCH_RESULTS_EVENT =
  'ReceiveConversationSearchResults';
export const RECEIVE_ERROR_EVENT = 'ReceiveError';
export const NEW_MESSAGE_NOTIFICATION_EVENT = 'NewMessageNotification';
export const RECEIVE_NOTIFICATION_EVENT = 'ReceiveNotification';
export const UPDATE_NOTIFICATION_READ_STATUS_EVENT =
  'UpdateNotificationReadStatus';
export const DELETE_NOTIFICATION_EVENT = 'DeleteNotification';

export const UNREGISTER_USER = 'UnregisterUser';
export const JOIN_GROUP_METHOD = 'JoinGroup';
export const REGISTER_USER_METHOD = 'RegisterUser';
export const SEND_MESSAGE_TO_GROUP_METHOD = 'SendMessageToGroup';
export const DELETE_MESSAGE_METHOD = 'DeleteMessage';
export const MESSAGE_DELETED_EVENT = 'MessageDeleted';
export const REACT_TO_MESSAGE_METHOD = 'ReactToMessage';
export const MESSAGE_REACTION_UPDATED_EVENT = 'MessageReactionUpdated';
export const ACKNOWLEDGE_DELIVERY_METHOD = 'AcknowledgeDelivery';
export const UPDATE_READ_STATUS_METHOD = 'UpdateReadStatus';
export const USER_DELIVERED_STATUS_UPDATED_EVENT = 'UserDeliveredStatusUpdated';
export const USER_READ_STATUS_UPDATED_EVENT = 'UserReadStatusUpdated';
export const LOAD_MEMBER_STATUSES_EVENT = 'LoadMemberStatuses';
export const LOAD_GROUP_MEMBERS_EVENT = 'LoadGroupMembers';
export const SET_TYPING_STATUS_METHOD = 'SetTypingStatus';
export const TYPING_STATUS_UPDATED_EVENT = 'TypingStatusUpdated';
export const GROUP_IMAGE_UPDATED_EVENT = 'GroupImageUpdated';
export const GROUP_TITLE_UPDATED_EVENT = 'GroupTitleUpdated';
export const GROUP_MEMBERS_UPDATED_EVENT = 'GroupMembersUpdated';
export const REMOVED_FROM_GROUP_EVENT = 'RemovedFromGroup';

export interface RemovedFromGroupEvent {
  groupId: number;
}

export interface GroupMembersUpdatedEvent {
  groupId: string;
}

export interface GroupImageUpdatedEvent {
  groupId: string;
  imageId: number;
  updatedByUserId: number;
}

export interface GroupTitleUpdatedEvent {
  groupId: string;
  newTitle: string;
}

export interface TypingStatusUpdatedEvent {
  groupId: string;
  userId: number;
  nickname: string;
  isTyping: boolean;
}

export interface GroupMember {
  userId: number;
  nickname: string;
  firstName?: string | null;
  lastName?: string | null;
  avatarThumbUrl?: string | null;
}

export interface MemberStatus {
  userId: number;
  lastDeliveredAt: string | null;
  lastReadAt: string | null;
}

export interface HistoryReaction {
  userId: number;
  reaction: string;
}

export interface HistoryMessage {
  messageId: number;
  kind?: number;
  userId: number;
  message: string;
  sentAt: string;
  senderName: string;
  isEdited?: boolean;
  modifiedAt?: string;
  replyToMessageId?: number | null;
  IsEdited?: boolean;
  ModifiedAt?: string;
  ReplyToMessageId?: number | null;
  reactions?: HistoryReaction[];
}

export interface LoadHistoryEvent {
  groupId: number;
  messages: HistoryMessage[];
}

export interface UserDeliveredStatusUpdatedEvent {
  groupId: number;
  userId: number;
  lastMessageId: number;
}

export interface UserReadStatusUpdatedEvent {
  groupId: number;
  userId: number;
  readTime: string;
}

export interface MessageReceipt {
  lastDeliveredMessageId: number;
  lastReadTime: number;
  lastDeliveredTime?: number;
}

// Hub DateTimes are UTC, including values serialized without an explicit suffix.
export function receiptUtcTime(value: string): number {
  if (typeof value !== 'string' || !value) return NaN;
  return Date.parse(
    /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`,
  );
}

export interface ReceiveMessageEvent {
  userId: number;
  groupId: number;
  message: string;
  dateCreated: string;
  messageId: number;
  senderName: string;
  replyToMessageId?: number | null;
}

export interface ConversationSearchResult {
  groupId: string;
  groupName: string | null;
  matchedUserName: string;
  groupImageUrl?: string | null;
  groupThumbUrl?: string | null;
}

export interface NewMessageNotificationEvent {
  groupId: number;
  message: string;
  sentAt: string;
  sentByUserId: number;
  replyToMessageId?: number | null;
  ReplyToMessageId?: number | null;
}

export interface MessageDeletedEvent {
  messageId: number;
  groupId: number;
}

export interface MessageReactionUpdatedEvent {
  messageId: number;
  userId: number;
  reaction: string | null;
}

export type MessageReactionState = Record<number, Record<number, string>>;

export interface ReceiveNotificationEvent {
  notificationId: number;
  isRead: boolean;
}

export interface ReceiveNotificationPayload {
  notification: ReceiveNotificationEvent;
  unreadCount: number;
}

export interface UpdateNotificationReadStatusEvent {
  notificationId: number;
  isRead: boolean;
}

export interface UpdateNotificationReadStatusPayload {
  notification: UpdateNotificationReadStatusEvent;
  unreadCount: number;
}

export type DeleteNotificationEvent = number;

// export interface DeleteNotificationPayload {
//   notificationId: DeleteNotificationEvent;
//   unreadCount?: number;
// }

@Injectable({
  providedIn: 'root',
})
export class SignalrService {
  private connection?: HubConnection;
  private startPromise?: Promise<void>;
  private initialRetryTimer?: ReturnType<typeof setTimeout>;
  private readonly joinedGroupIds = new Set<string | number>();
  private readonly removedGroupIds = new Set<number>();
  private hasConnectedOnce = false;
  private sessionService = inject(SessionService);
  private accountService = inject(AccountService);
  private recipientIdPromise?: Promise<number>;
  private receivedMessages = new Map<number, Map<number, number>>();
  private recipientRetryTimer?: ReturnType<typeof setTimeout>;
  private receiptSession = 0;
  private pendingDeliveries = new Map<number, number>();
  private acknowledgedDeliveries = new Map<number, number>();
  private deliveryFlush?: Promise<void>;
  private deliveryRetryTimer?: ReturnType<typeof setTimeout>;
  private nativeAppActive = true;
  private visibilitySubject = new BehaviorSubject(
    document.visibilityState !== 'hidden',
  );
  readonly appVisible$ = this.visibilitySubject.asObservable();
  readonly receipts = signal<Record<string, MessageReceipt>>({});
  private readonly reactionState = signal<MessageReactionState>({});
  readonly messageReactions = this.reactionState.asReadonly();
  // JoinGroup emits members before the user opens the details page.
  private readonly groupMembersState = signal<Record<number, GroupMember[]>>({});
  readonly groupMembers = this.groupMembersState.asReadonly();
  private deliveredSubject = new Subject<UserDeliveredStatusUpdatedEvent>();
  private readSubject = new Subject<UserReadStatusUpdatedEvent>();
  private typingSubject = new Subject<TypingStatusUpdatedEvent>();
  private groupImageUpdatedSubject = new Subject<GroupImageUpdatedEvent>();
  private groupTitleUpdatedSubject = new Subject<GroupTitleUpdatedEvent>();
  private groupMembersUpdatedSubject = new Subject<GroupMembersUpdatedEvent>();
  private removedFromGroupSubject = new Subject<RemovedFromGroupEvent>();

  onRemovedFromGroup() {
    return this.removedFromGroupSubject.asObservable();
  }

  wasRemovedFromGroup(groupId: string | number) {
    return this.removedGroupIds.has(Number(groupId));
  }

  markRemovedFromGroup(groupId: number) {
    if (!Number.isSafeInteger(groupId) || groupId <= 0 || this.wasRemovedFromGroup(groupId)) return;
    this.removedGroupIds.add(groupId);
    this.forgetGroup(groupId);
    this.removedFromGroupSubject.next({ groupId });
  }

  onGroupMembersUpdated() {
    return this.groupMembersUpdatedSubject.asObservable();
  }

  onGroupImageUpdated() {
    return this.groupImageUpdatedSubject.asObservable();
  }

  onGroupTitleUpdated() {
    return this.groupTitleUpdatedSubject.asObservable();
  }
  // Preserve the existing registration/join request lifecycle.
  private historyRequestQueue: Promise<void> = Promise.resolve();
  private historyRequestGeneration = 0;
  private memberStatusesEnabled = false;

  constructor() {
    const destroyRef = inject(DestroyRef);
    const visibilityListener = () => this.publishVisibility();
    document.addEventListener('visibilitychange', visibilityListener);
    destroyRef.onDestroy(() => {
      document.removeEventListener('visibilitychange', visibilityListener);
      if (this.deliveryRetryTimer) clearTimeout(this.deliveryRetryTimer);
      if (this.recipientRetryTimer) clearTimeout(this.recipientRetryTimer);
    });
    if (Capacitor.isNativePlatform()) {
      const listener = App.addListener('appStateChange', ({ isActive }) => {
        this.nativeAppActive = isActive;
        this.publishVisibility();
      });
      destroyRef.onDestroy(() => {
        void listener.then((handle) => handle.remove());
      });
      void App.getState()
        .then(({ isActive }) => {
          this.nativeAppActive = isActive;
          this.publishVisibility();
        })
        .catch((error) =>
          environment.log('[SignalR] App visibility unavailable:', error),
        );
    }
  }

  isAppVisible() {
    return this.visibilitySubject.value;
  }

  private publishVisibility() {
    const visible =
      this.nativeAppActive && document.visibilityState !== 'hidden';
    if (visible !== this.visibilitySubject.value)
      this.visibilitySubject.next(visible);
  }

  onUserDeliveredStatusUpdated() {
    return this.deliveredSubject.asObservable();
  }

  onUserReadStatusUpdated() {
    return this.readSubject.asObservable();
  }

  onTypingStatusUpdated() {
    return this.typingSubject.asObservable();
  }

  async setTypingStatus(groupId: string, isTyping: boolean): Promise<void> {
    if (this.wasRemovedFromGroup(groupId)) return;
    await this.getConnectedConnection().invoke(
      SET_TYPING_STATUS_METHOD,
      groupId,
      isTyping,
    );
  }

  private hydrateHistoryReactions(messages: HistoryMessage[]) {
    const snapshots = messages.filter(
      (message) =>
        Number.isInteger(message.messageId) &&
        message.messageId > 0 &&
        Array.isArray(message.reactions),
    );
    if (!snapshots.length) return;
    this.reactionState.update((state) => {
      const next = { ...state };
      for (const message of snapshots) {
        const users: Record<number, string> = {};
        for (const entry of message.reactions ?? []) {
          if (
            !entry ||
            !Number.isInteger(entry.userId) ||
            entry.userId <= 0 ||
            typeof entry.reaction !== 'string' ||
            !entry.reaction
          )
            continue;
          // Preserve the exact server string for toggle-off requests.
          users[entry.userId] = entry.reaction;
        }
        // A history snapshot replaces cached reactions for this message only.
        if (Object.keys(users).length) next[message.messageId] = users;
        else delete next[message.messageId];
      }
      return next;
    });
  }

  private updateReceipt(
    groupId: number,
    userId: number,
    update: Partial<MessageReceipt>,
  ) {
    if (this.wasRemovedFromGroup(groupId)) return;
    const key = `${groupId}:${userId}`;
    this.receipts.update((receipts) => {
      const previous = receipts[key];
      return {
        ...receipts,
        [key]: {
          lastDeliveredMessageId: Math.max(
            previous?.lastDeliveredMessageId ?? 0,
            update.lastDeliveredMessageId ?? 0,
          ),
          lastReadTime: Math.max(
            previous?.lastReadTime ?? 0,
            update.lastReadTime ?? 0,
          ),
          lastDeliveredTime: Math.max(
            previous?.lastDeliveredTime ?? 0,
            update.lastDeliveredTime ?? 0,
          ),
        },
      };
    });
  }

  /** Also used by history/background fetches after actual message content arrives. */
  async acknowledgeReceivedMessages(
    groupId: number,
    messages: { messageId: number; userId: number }[],
  ) {
    if (!this.sessionService.get(Storage.SESSION_TOKEN) || this.wasRemovedFromGroup(groupId)) return;
    for (const message of messages) {
      const senderId = message.userId;
      const messageId = message.messageId;
      const senders =
        this.receivedMessages.get(groupId) ?? new Map<number, number>();
      senders.set(senderId, Math.max(senders.get(senderId) ?? 0, messageId));
      this.receivedMessages.set(groupId, senders);
    }
    if (!this.receivedMessages.size) return;
    const session = this.receiptSession;
    try {
      const recipientId = await (this.recipientIdPromise ??= firstValueFrom(
        this.accountService.apiV5AccountProfileGet(),
      )
        .then((profile) => profile.userId)
        .catch((error) => {
          this.recipientIdPromise = undefined;
          throw error;
        }));
      if (session !== this.receiptSession) return;
      for (const [receivedGroupId, senders] of this.receivedMessages) {
        let lastMessageId = 0;
        for (const [senderId, messageId] of senders) {
          if (senderId !== recipientId)
            lastMessageId = Math.max(lastMessageId, messageId);
        }
        if (lastMessageId > 0)
          this.acknowledgeDelivery(receivedGroupId, lastMessageId);
      }
      this.receivedMessages.clear();
    } catch (error) {
      environment.log(
        '[SignalR] Unable to identify delivery recipient:',
        error,
      );
      if (session === this.receiptSession && !this.recipientRetryTimer) {
        this.recipientRetryTimer = setTimeout(() => {
          this.recipientRetryTimer = undefined;
          void this.acknowledgeReceivedMessages(groupId, []);
        }, 5000);
      }
    }
  }

  acknowledgeDelivery(groupId: number, lastMessageId: number) {
    if (this.wasRemovedFromGroup(groupId)) return;
    if (lastMessageId <= (this.acknowledgedDeliveries.get(groupId) ?? 0))
      return;
    this.pendingDeliveries.set(
      groupId,
      Math.max(this.pendingDeliveries.get(groupId) ?? 0, lastMessageId),
    );
    void this.flushDeliveries();
  }

  private flushDeliveries(): Promise<void> {
    if (this.deliveryFlush) return this.deliveryFlush;
    if (!this.isConnected() || !this.pendingDeliveries.size)
      return Promise.resolve();
    const session = this.receiptSession;
    const flush = (async () => {
      for (const [groupId, lastMessageId] of this.pendingDeliveries) {
        if (session !== this.receiptSession || !this.isConnected()) break;
        try {
          await this.getConnectedConnection().invoke(
            ACKNOWLEDGE_DELIVERY_METHOD,
            groupId,
            lastMessageId,
          );
          if (session !== this.receiptSession) return;
          if (this.wasRemovedFromGroup(groupId)) continue;
          this.acknowledgedDeliveries.set(groupId, lastMessageId);
          if (this.pendingDeliveries.get(groupId) === lastMessageId)
            this.pendingDeliveries.delete(groupId);
        } catch (error) {
          environment.log(
            '[SignalR] AcknowledgeDelivery failed; will retry:',
            error,
          );
          break;
        }
      }
    })().finally(() => {
      if (this.deliveryFlush !== flush) return;
      this.deliveryFlush = undefined;
      if (this.pendingDeliveries.size && !this.deliveryRetryTimer) {
        this.deliveryRetryTimer = setTimeout(() => {
          this.deliveryRetryTimer = undefined;
          void this.flushDeliveries();
        }, 5000);
      }
    });
    this.deliveryFlush = flush;
    return flush;
  }

  updateReadStatus = async (groupId: string | number) => {
    if (!this.isAppVisible() || !this.joinedGroupIds.has(groupId)) return;
    await this.getConnectedConnection().invoke(
      UPDATE_READ_STATUS_METHOD,
      String(groupId),
    );
  };

  public isConnected = () =>
    this.connection?.state === HubConnectionState.Connected;
  public getConnectionState = () => this.connection?.state || 'Disconnected';

  private getBearerToken = () =>
    `Bearer ${this.sessionService.get(Storage.SESSION_TOKEN)}`;
  private _onReceiveMessageSubject = new Subject<ReceiveMessageEvent>();
  public onReceiveMessage(): Observable<ReceiveMessageEvent> {
    return this._onReceiveMessageSubject.asObservable();
  }

  private conversationSearchResultsSubject =
    new Subject<ConversationSearchResult[]>();
  onReceiveConversationSearchResults() {
    return this.conversationSearchResultsSubject.asObservable();
  }

  async searchConversations(searchTerm: string): Promise<void> {
    await this.getConnectedConnection().invoke(
      SEARCH_CONVERSATIONS_METHOD,
      searchTerm,
    );
  }

  private _onNewMessageNotificationSubject =
    new Subject<NewMessageNotificationEvent>();
  public onNewMessageNotification(): Observable<NewMessageNotificationEvent> {
    return this._onNewMessageNotificationSubject.asObservable();
  }

  private _onLoadHistorySubject = new Subject<LoadHistoryEvent>();
  public onLoadHistory(): Observable<LoadHistoryEvent> {
    return this._onLoadHistorySubject.asObservable();
  }

  private _onUnreadCountUpdatedSubject = new Subject<number>();
  public onUnreadCountUpdated(): Observable<number> {
    return this._onUnreadCountUpdatedSubject.asObservable();
  }

  private _onReconnectingSubject = new Subject<void>();
  public onReconnecting(): Observable<void> {
    return this._onReconnectingSubject.asObservable();
  }

  private _onConnectionRestoredSubject = new Subject<void>();
  public onConnectionRestored(): Observable<void> {
    return this._onConnectionRestoredSubject.asObservable();
  }

  private _onMessageEditedSubject = new Subject<{
    messageId: number;
    newContent: string;
    modifiedAt: string;
  }>();
  public onMessageEdited() {
    return this._onMessageEditedSubject.asObservable();
  }

  private _onMessageDeletedSubject = new Subject<number>();
  public onMessageDeleted(): Observable<number> {
    return this._onMessageDeletedSubject.asObservable();
  }

  private _onReceiveErrorSubject = new Subject<string>();
  public onReceiveError(): Observable<string> {
    return this._onReceiveErrorSubject.asObservable();
  }

  private _onReceiveNotificationSubject =
    new Subject<ReceiveNotificationPayload>();
  public onReceiveNotification(): Observable<ReceiveNotificationPayload> {
    return this._onReceiveNotificationSubject.asObservable();
  }

  private _onUpdateNotificationReadStatusSubject =
    new Subject<UpdateNotificationReadStatusPayload>();
  public onUpdateNotificationReadStatus(): Observable<UpdateNotificationReadStatusPayload> {
    return this._onUpdateNotificationReadStatusSubject.asObservable();
  }

  // private _onDeleteNotificationSubject = new Subject<DeleteNotificationPayload>();
  // public onDeleteNotification(): Observable<DeleteNotificationPayload> {
  //   return this._onDeleteNotificationSubject.asObservable();
  // }

  init(): Promise<void> {
    if (!this.connection) {
      this.buildConnection();
    }

    if (this.connection?.state === HubConnectionState.Connected) {
      return this.registerUserIfLoggedIn();
    }

    if (this.connection?.state !== HubConnectionState.Disconnected) {
      return this.startPromise ?? Promise.resolve();
    }

    if (this.startPromise) {
      return this.startPromise;
    }

    const startPromise = this.connection
      .start()
      .then(() => this.restoreConnectionState())
      .catch((error) => {
        this.scheduleInitialConnectionRetry();
        throw error;
      })
      .finally(() => {
        if (this.startPromise === startPromise) {
          this.startPromise = undefined;
        }
      });

    this.startPromise = startPromise;
    return startPromise;
  }

  private buildConnection() {
    const conn = new HubConnectionBuilder()
      .withUrl(`${environment.wsBaseUrl}/chat`)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: ({ previousRetryCount }) => {
          const delays = [0, 2000, 5000, 10000, 15000];
          return delays[Math.min(previousRetryCount, delays.length - 1)];
        },
      });

    if (!environment.production) {
      conn.configureLogging(LogLevel.Information);
    }
    const connection = conn.build();
    this.connection = connection;

    connection.on(GROUP_IMAGE_UPDATED_EVENT, (event: GroupImageUpdatedEvent) => {
      this.groupImageUpdatedSubject.next(event);
    });
    connection.on(GROUP_TITLE_UPDATED_EVENT, (groupId: string, newTitle: string) => {
      this.groupTitleUpdatedSubject.next({ groupId, newTitle });
    });
    connection.on(GROUP_MEMBERS_UPDATED_EVENT, (groupId: string) => {
      if (!this.memberStatusesEnabled || this.wasRemovedFromGroup(groupId)) return;
      this.invalidateGroupMembers(Number(groupId));
      this.groupMembersUpdatedSubject.next({ groupId });
    });
    connection.on(REMOVED_FROM_GROUP_EVENT, (rawGroupId: string | number) => {
      if (!this.memberStatusesEnabled) return;
      this.markRemovedFromGroup(Number(rawGroupId));
    });

    connection.on(
      TYPING_STATUS_UPDATED_EVENT,
      (groupId: string, userId: number, nickname: string, isTyping: boolean) => {
        this.typingSubject.next({ groupId, userId, nickname, isTyping });
      },
    );

    connection.on(
      LOAD_GROUP_MEMBERS_EVENT,
      (rawGroupId: string | number, members: GroupMember[]) => {
        if (!this.memberStatusesEnabled || this.wasRemovedFromGroup(rawGroupId)) return;
        this.groupMembersState.update((groups) => ({
          ...groups,
          [Number(rawGroupId)]: members,
        }));
      },
    );

    connection.on(
      RECEIVE_CONVERSATION_SEARCH_RESULTS_EVENT,
      (results: ConversationSearchResult[]) => {
        this.conversationSearchResultsSubject.next(results);
      },
    );

    connection.on(
      LOAD_MEMBER_STATUSES_EVENT,
      (rawGroupId: string | number, statuses: MemberStatus[]) => {
        if (!this.memberStatusesEnabled) return;
        const groupId = Number(rawGroupId);
        let applied = 0;
        for (const status of statuses) {
          const delivered = receiptUtcTime(status.lastDeliveredAt ?? '');
          const read = receiptUtcTime(status.lastReadAt ?? '');
          const update: Partial<MessageReceipt> = {};
          if (Number.isFinite(delivered) && delivered > 0)
            update.lastDeliveredTime = delivered;
          if (Number.isFinite(read) && read > 0) update.lastReadTime = read;
          if (!Object.keys(update).length) continue;
          this.updateReceipt(groupId, status.userId, update);
          applied++;
        }
        environment.log('[SignalR] LoadMemberStatuses applied:', {
          groupId,
          members: applied,
        });
      },
    );

    connection.on(
      USER_DELIVERED_STATUS_UPDATED_EVENT,
      (groupId: number, userId: number, lastMessageId: number) => {
        const event = {
          groupId: groupId,
          userId: userId,
          lastMessageId: lastMessageId,
        };
        environment.log('[SignalR] UserDeliveredStatusUpdated:', event);
        this.updateReceipt(event.groupId, event.userId, {
          lastDeliveredMessageId: event.lastMessageId,
        });
        this.deliveredSubject.next(event);
      },
    );
    connection.on(
      USER_READ_STATUS_UPDATED_EVENT,
      (groupId: string | number, userId: number, readTime: string) => {
        const event = {
          groupId: Number(groupId),
          userId: userId,
          readTime,
        };
        const time = receiptUtcTime(readTime);
        if (!Number.isFinite(time)) return;
        environment.log('[SignalR] UserReadStatusUpdated:', event);
        this.updateReceipt(event.groupId, event.userId, { lastReadTime: time });
        this.readSubject.next(event);
      },
    );

    connection.on(
      LOAD_HISTORY_EVENT,
      (messages: HistoryMessage[], rawGroupId: string) => {
        const groupId = Number(rawGroupId);
        if (this.wasRemovedFromGroup(groupId)) return;
        this.hydrateHistoryReactions(messages);
        void this.acknowledgeReceivedMessages(groupId, messages);
        this._onLoadHistorySubject.next({ groupId, messages });
      },
    );

    connection.on(
      RECEIVE_MESSAGE_EVENT,
      (
        userId: number,
        rawGroupId: string | number,
        message: string,
        dateCreated: string,
        messageId: number,
        senderName: string,
        replyToMessageId: number | null = null,
      ) => {
        const groupId = Number(rawGroupId);
        if (this.wasRemovedFromGroup(groupId)) return;
        void this.acknowledgeReceivedMessages(groupId, [{ messageId, userId }]);
        this._onReceiveMessageSubject.next({
          userId,
          groupId: groupId,
          message,
          dateCreated,
          messageId,
          senderName,
          replyToMessageId,
        });
      },
    );

    connection.on(
      NEW_MESSAGE_NOTIFICATION_EVENT,
      (data: NewMessageNotificationEvent) => {
        if (this.wasRemovedFromGroup(data.groupId)) return;
        this._onNewMessageNotificationSubject.next({
          ...data,
          replyToMessageId:
            data.replyToMessageId ?? data.ReplyToMessageId ?? null,
        });
      },
    );

    connection.on(RECEIVE_ERROR_EVENT, (message: string) => {
      this._onReceiveErrorSubject.next(message);
    });

    connection.on(
      'MessageEdited',
      (messageId: number, newContent: string, modifiedAt: string) => {
        this._onMessageEditedSubject.next({
          messageId,
          newContent,
          modifiedAt,
        });
      },
    );

    connection.on(MESSAGE_DELETED_EVENT, (data: number) => {
      this.reactionState.update((state) => {
        const next = { ...state };
        delete next[data];
        return next;
      });
      this._onMessageDeletedSubject.next(data);
    });

    connection.on(
      MESSAGE_REACTION_UPDATED_EVENT,
      (
        messageIdOrEvent: number | MessageReactionUpdatedEvent,
        userId?: number,
        reaction?: string | null,
      ) => {
        const event =
          typeof messageIdOrEvent === 'number'
            ? { messageId: messageIdOrEvent, userId, reaction }
            : messageIdOrEvent;
        if (
          !event ||
          !Number.isInteger(event.messageId) ||
          event.messageId <= 0 ||
          typeof event.userId !== 'number' ||
          !Number.isInteger(event.userId) ||
          (event.reaction !== null && typeof event.reaction !== 'string')
        )
          return;
        environment.log('[SignalR] MessageReactionUpdated:', event);
        const { messageId, userId: reactingUserId, reaction: value } = event;
        this.reactionState.update((state) => {
          const users = { ...state[messageId] };
          if (value === null) delete users[reactingUserId];
          else users[reactingUserId] = value;
          const next = { ...state };
          if (Object.keys(users).length) next[messageId] = users;
          else delete next[messageId];
          return next;
        });
      },
    );

    connection.on(
      RECEIVE_NOTIFICATION_EVENT,
      (notification: ReceiveNotificationEvent, unreadCount: number) => {
        // environment.log('[SignalR] ReceiveNotification:', {
        //   notification,
        //   unreadCount,
        // });
        this._onReceiveNotificationSubject.next({
          notification,
          unreadCount,
        });
      },
    );

    connection.on(
      UPDATE_NOTIFICATION_READ_STATUS_EVENT,
      (notificationId: number, isRead: boolean, unreadCount: number) => {
        const notification: UpdateNotificationReadStatusEvent = {
          notificationId,
          isRead,
        };
        // environment.log('[SignalR] UpdateNotificationReadStatus:', {
        //   notification,
        //   unreadCount,
        // });
        this._onUpdateNotificationReadStatusSubject.next({
          notification,
          unreadCount,
        });
      },
    );

    // DELETE_NOTIFICATION_EVENT is not wired yet because backend payload shape is not finalized.

    connection.onreconnecting(() => {
      this.invalidateHistoryRequests();
      this._onReconnectingSubject.next();
    });

    connection.onreconnected(async () => {
      await this.restoreConnectionState();
    });

    connection.onclose(() => {
      this.invalidateHistoryRequests();
      this.scheduleInitialConnectionRetry();
    });
  }

  private scheduleInitialConnectionRetry() {
    if (
      this.initialRetryTimer ||
      !this.sessionService.get(Storage.SESSION_TOKEN)
    ) {
      return;
    }

    this.initialRetryTimer = setTimeout(() => {
      this.initialRetryTimer = undefined;
      void this.init().catch((error) => {
        environment.log('[SignalR] Connection retry failed:', error);
      });
    }, 5000);
  }

  private async restoreConnectionState(): Promise<void> {
    const connectionWasRestored = this.hasConnectedOnce;

    if (this.initialRetryTimer) {
      clearTimeout(this.initialRetryTimer);
      this.initialRetryTimer = undefined;
    }

    await this.registerUserIfLoggedIn();

    for (const groupId of this.joinedGroupIds) {
      if (!this.isAppVisible()) break;
      try {
        await this.invokeHistoryRequest(JOIN_GROUP_METHOD, groupId, [
          String(groupId),
        ]);
      } catch (error) {
        console.error(`[SignalR] Failed to rejoin group ${groupId}:`, error);
      }
    }

    this.hasConnectedOnce = true;
    void this.flushDeliveries();
    if (connectionWasRestored) {
      this._onConnectionRestoredSubject.next();
    }
  }

  private async registerUserIfLoggedIn(): Promise<void> {
    const token = this.sessionService.get(Storage.SESSION_TOKEN);
    if (token) {
      try {
        await this.registerUser();
      } catch (error) {
        console.error('[SignalR] Failed to register user:', error);
      }
    } else {
      console.warn('[SignalR] No token available for user registration');
    }
  }

  notifyUnreadCountUpdated(newUnreadCount: number) {
    this._onUnreadCountUpdatedSubject.next(newUnreadCount);
  }

  joinGroup = async (groupId: string | number) => {
    if (this.wasRemovedFromGroup(groupId)) return;
    this.joinedGroupIds.add(groupId);
    if (!this.isAppVisible()) return;

    if (!this.isConnected()) {
      void this.init().catch((error) => {
        environment.log(
          '[SignalR] Waiting to join group after reconnect:',
          error,
        );
      });
      return;
    }

    await this.invokeHistoryRequest(JOIN_GROUP_METHOD, groupId, [
      String(groupId),
    ]);
  };

  leaveGroup = (groupId: string | number) => {
    this.joinedGroupIds.delete(groupId);
  };

  forgetGroup(groupId: number) {
    this.joinedGroupIds.delete(groupId);
    this.joinedGroupIds.delete(String(groupId));
    this.invalidateGroupMembers(groupId);
    this.pendingDeliveries.delete(groupId);
    this.acknowledgedDeliveries.delete(groupId);
    this.receivedMessages.delete(groupId);
    this.receipts.update((receipts) => Object.fromEntries(
      Object.entries(receipts).filter(([key]) => !key.startsWith(`${groupId}:`)),
    ));
  }

  private invalidateGroupMembers(groupId: number) {
    this.groupMembersState.update((groups) => {
      const next = { ...groups };
      delete next[groupId];
      return next;
    });
  }

  loadHistory = async (groupId: string | number, skip = 0, take = 50) =>
    await this.invokeHistoryRequest(LOAD_HISTORY_EVENT, groupId, [
      String(groupId),
      skip,
      take,
    ]);

  private invalidateHistoryRequests() {
    this.historyRequestGeneration++;
  }

  private invokeHistoryRequest(
    method: string,
    groupId: string | number | undefined,
    args: unknown[],
  ): Promise<void> {
    const generation = this.historyRequestGeneration;
    const request = this.historyRequestQueue.then(async () => {
      if (generation !== this.historyRequestGeneration)
        throw new Error(
          'History request cancelled after session/connection change.',
        );
      // A membership update can queue a join just before removal arrives.
      if (groupId != null && this.wasRemovedFromGroup(groupId)) return;
      await this.getConnectedConnection().invoke(method, ...args);
    });
    // A failed request must not prevent the next chat from loading.
    this.historyRequestQueue = request.catch(() => {});
    return request;
  }

  unregisterUser = async () => {
    this.memberStatusesEnabled = false;
    this.invalidateHistoryRequests();
    this.joinedGroupIds.clear();
    this.removedGroupIds.clear();
    this.receiptSession++;
    this.recipientIdPromise = undefined;
    this.receivedMessages.clear();
    if (this.recipientRetryTimer) clearTimeout(this.recipientRetryTimer);
    this.recipientRetryTimer = undefined;
    this.pendingDeliveries.clear();
    this.acknowledgedDeliveries.clear();
    this.receipts.set({});
    this.reactionState.set({});
    this.groupMembersState.set({});
    this.deliveryFlush = undefined;
    if (this.deliveryRetryTimer) clearTimeout(this.deliveryRetryTimer);
    this.deliveryRetryTimer = undefined;
    if (this.isConnected()) {
      await this.connection?.invoke(UNREGISTER_USER);
    }
  };

  registerUser = async () => {
    this.memberStatusesEnabled = true;
    await this.invokeHistoryRequest(REGISTER_USER_METHOD, undefined, [
      this.getBearerToken(),
    ]);
  };

  sendMessageToGroup = async (
    groupId: string | number,
    message: string,
    replyToMessageId: number | null = null,
  ) => {
    if (this.wasRemovedFromGroup(groupId))
      throw new Error('You were removed from this group.');
    await this.getConnectedConnection().invoke(
      SEND_MESSAGE_TO_GROUP_METHOD,
      String(groupId),
      message,
      replyToMessageId,
    );
  };

  editMessage = async (messageId: number, newContent: string) => {
    await this.getConnectedConnection().invoke(
      'EditMessage',
      messageId,
      newContent,
    );
  };

  deleteMessage = async (messageId: number) => {
    await this.getConnectedConnection().invoke(
      DELETE_MESSAGE_METHOD,
      messageId,
    );
  };

  reactToMessage = async (messageId: number, reaction: string) => {
    environment.log('[SignalR] ReactToMessage:', { messageId, reaction });
    await this.getConnectedConnection().invoke(
      REACT_TO_MESSAGE_METHOD,
      messageId,
      reaction,
    );
  };

  private getConnectedConnection(): HubConnection {
    if (!this.connection || !this.isConnected()) {
      throw new Error('SignalR connection is temporarily unavailable.');
    }

    return this.connection;
  }
}
